import * as THREE from "three";

interface Particle {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
  v: THREE.Vector3;
  rot: THREE.Vector3;
}

export class EffectsSystem {
  group = new THREE.Group();
  private particles: Particle[] = [];
  private bulletPool: THREE.InstancedMesh;
  private maxBullets = 256;
  private bulletDummy = new THREE.Object3D();

  constructor() {
    this.group.name = "Effects";
    const bulletGeo = new THREE.SphereGeometry(0.45, 6, 4);
    const bulletMat = new THREE.MeshBasicMaterial({ color: 0xffe0a0 });
    this.bulletPool = new THREE.InstancedMesh(bulletGeo, bulletMat, this.maxBullets);
    this.bulletPool.frustumCulled = false;
    this.bulletPool.count = 0;
    this.group.add(this.bulletPool);
  }

  setBullets(positions: { x: number; y: number; z: number; kind: 0 | 1 }[]) {
    const count = Math.min(positions.length, this.maxBullets);
    for (let i = 0; i < count; i++) {
      const p = positions[i];
      this.bulletDummy.position.set(p.x, p.y, p.z);
      this.bulletDummy.scale.setScalar(p.kind === 1 ? 1.5 : 1.0);
      this.bulletDummy.updateMatrix();
      this.bulletPool.setMatrixAt(i, this.bulletDummy.matrix);
    }
    this.bulletPool.count = count;
    this.bulletPool.instanceMatrix.needsUpdate = true;
  }

  spawnExplosion(x: number, y: number, z: number, radius: number) {
    const colors = [0xffd166, 0xff7a40, 0xffffff, 0xff5252];
    const count = Math.min(28, Math.floor(radius * 2));
    for (let i = 0; i < count; i++) {
      const c = colors[i % colors.length];
      const geo = new THREE.TetrahedronGeometry(0.35 + Math.random() * 0.4, 0);
      const mat = new THREE.MeshBasicMaterial({ color: c });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, z);
      const dir = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 1.2,
        (Math.random() - 0.5) * 2,
      ).normalize().multiplyScalar(8 + Math.random() * radius * 1.6);
      const rot = new THREE.Vector3((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6);
      this.group.add(mesh);
      this.particles.push({ mesh, life: 0.8 + Math.random() * 0.6, maxLife: 1.4, v: dir, rot });
    }
    // Shockwave ring
    const ringGeo = new THREE.RingGeometry(0.5, 0.7, 16);
    ringGeo.rotateX(-Math.PI / 2);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xfff0c0, transparent: true, opacity: 0.85, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.set(x, y, z);
    this.group.add(ring);
    this.particles.push({
      mesh: ring,
      life: 0.6, maxLife: 0.6,
      v: new THREE.Vector3(0, 0, 0),
      rot: new THREE.Vector3(0, 0, 0),
    });
    (ring as any).__isRing = true;
    (ring as any).__radius = radius;
  }

  spawnExhaust(pos: THREE.Vector3, vel: THREE.Vector3) {
    if (Math.random() > 0.5) return;
    const geo = new THREE.TetrahedronGeometry(0.18, 0);
    const mat = new THREE.MeshBasicMaterial({ color: 0xa8b5ff, transparent: true, opacity: 0.7 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    this.group.add(mesh);
    this.particles.push({
      mesh, life: 0.5, maxLife: 0.5,
      v: vel.clone().multiplyScalar(-0.4).add(new THREE.Vector3((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4)),
      rot: new THREE.Vector3(0, 0, 0),
    });
  }

  // Bright cyan after-burner trail emitted while a plane is dashing.
  spawnDashTrail(pos: THREE.Vector3, vel: THREE.Vector3) {
    const geo = new THREE.TetrahedronGeometry(0.45 + Math.random() * 0.25, 0);
    const colors = [0x5cf2ff, 0xb5fbff, 0x80e8ff, 0xffe7a0];
    const mat = new THREE.MeshBasicMaterial({
      color: colors[Math.floor(Math.random() * colors.length)],
      transparent: true, opacity: 1,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    this.group.add(mesh);
    this.particles.push({
      mesh, life: 0.7, maxLife: 0.7,
      v: vel.clone().multiplyScalar(-0.6).add(new THREE.Vector3((Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6)),
      rot: new THREE.Vector3(0, 0, 0),
    });
  }

  step(dt: number) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.group.remove(p.mesh);
        (p.mesh.geometry as THREE.BufferGeometry).dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.particles.splice(i, 1);
        continue;
      }
      const t = 1 - p.life / p.maxLife;
      if ((p.mesh as any).__isRing) {
        const r = (p.mesh as any).__radius * (0.4 + t * 1.6);
        p.mesh.scale.setScalar(r);
        (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
      } else {
        p.mesh.position.x += p.v.x * dt;
        p.mesh.position.y += p.v.y * dt;
        p.mesh.position.z += p.v.z * dt;
        p.v.y -= 5 * dt;
        p.mesh.rotation.x += p.rot.x * dt;
        p.mesh.rotation.y += p.rot.y * dt;
        p.mesh.rotation.z += p.rot.z * dt;
        const m = p.mesh.material as THREE.MeshBasicMaterial;
        if ((m as any).transparent !== false) {
          m.transparent = true;
          m.opacity = Math.max(0, 1 - t);
        }
      }
    }
  }
}
